import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, AlertTriangle, CheckCircle2, ListChecks, X, RefreshCw } from "lucide-react";

const HEALTH_TONE = {
  green:  { bg: "bg-emerald-50",  fg: "text-emerald-800", dot: "bg-emerald-500",  label: "Healthy" },
  amber:  { bg: "bg-amber-50",    fg: "text-amber-800",   dot: "bg-amber-500",    label: "Watch"   },
  red:    { bg: "bg-rose-50",     fg: "text-rose-800",    dot: "bg-rose-500",     label: "At Risk" },
};

/** Compact AI insights panel embedded inside OpportunityDetail. */
export default function OpportunityAiInsights({ oppId }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  async function generate() {
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/opportunities/${oppId}/insights`, {});
      setInsights(data);
      setExpanded(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!insights) {
    return (
      <button
        type="button"
        onClick={generate}
        disabled={loading}
        data-testid="opp-ai-insights-generate"
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#1F4E2C] to-[#0F1A12] text-white font-mono text-[10px] uppercase tracking-widest hover:shadow-lg transition-shadow disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {loading ? "Analysing…" : "AI Insights"}
      </button>
    );
  }

  const tone = HEALTH_TONE[insights.health] || HEALTH_TONE.amber;

  return (
    <div
      className={"mb-6 border " + tone.bg + " " + tone.fg + " border-current/20"}
      data-testid="opp-ai-insights-panel"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-current/15">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Gemini 3.1 · Deal Analyst</span>
          <span className={"inline-block w-2 h-2 rounded-full ml-2 " + tone.dot} />
          <span className="font-display font-bold text-sm" data-testid="opp-ai-insights-health">{tone.label}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={generate}
            disabled={loading}
            data-testid="opp-ai-insights-refresh"
            className="p-1.5 opacity-70 hover:opacity-100 disabled:opacity-40"
            title="Re-generate"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-widest opacity-80 hover:opacity-100 px-2"
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
          <button
            onClick={() => setInsights(null)}
            data-testid="opp-ai-insights-close"
            className="p-1.5 opacity-70 hover:opacity-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-3 space-y-3">
          <div className="text-sm leading-relaxed" data-testid="opp-ai-insights-summary">
            {insights.summary}
          </div>

          {insights.risks?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-80 mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Risks
              </div>
              <ul className="text-[13px] space-y-1" data-testid="opp-ai-insights-risks">
                {insights.risks.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="opacity-60 flex-shrink-0">·</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.next_actions?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-80 mb-1.5 flex items-center gap-1">
                <ListChecks className="w-3 h-3" /> Next actions
              </div>
              <ul className="text-[13px] space-y-1" data-testid="opp-ai-insights-actions">
                {insights.next_actions.map((a, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-3 h-3 mt-1 flex-shrink-0 opacity-60" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="font-mono text-[9px] opacity-60 pt-1 border-t border-current/10">
            Generated {new Date(insights.generated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {insights.model}
          </div>
        </div>
      )}
    </div>
  );
}
